import { AnimatePresence } from 'framer-motion'
import { ReactNode, useState } from 'react'
import { useTheme } from 'styled-components'

import useClickOutside from 'shared/hooks/useClickOutside'
import IcomoonIcon from 'shared/ui/IcomoonIcon'

import {
  Chevron,
  Container,
  Menu,
  OptionButton,
  OptionLabel,
  Trigger,
  TriggerContent
} from './Styles'

export type DropdownOption<T extends string = string> = {
  value: T
  label?: string
  icon?: ReactNode
  disabled?: boolean
}

type DropdownProps<T extends string> = {
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  renderTrigger?: (args: {
    selected?: DropdownOption<T>
    isOpen: boolean
  }) => ReactNode
  fullWidth?: boolean
  disabled?: boolean
}

const listVariants = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.1,
      staggerChildren: 0.03,
      staggerDirection: 1
    }
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.12,
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
}

const optionVariants = {
  hidden: { opacity: 0, y: -4 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.12 } }
}

function Dropdown<T extends string>({
  value,
  options,
  onChange,
  renderTrigger,
  fullWidth = false,
  disabled = false
}: DropdownProps<T>) {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useClickOutside(() => setIsOpen(false))

  const selected = options.find(option => option.value === value)

  const handleTriggerClick = () => {
    if (disabled) {
      return
    }

    setIsOpen(!isOpen)
  }

  const handleSelect = (option: DropdownOption<T>) => {
    if (option.disabled) {
      return
    }

    setIsOpen(false)

    if (option.value !== value) {
      onChange(option.value)
    }
  }

  return (
    <Container ref={ref} fullWidth={fullWidth}>
      <Trigger
        type="button"
        disabled={disabled}
        onClick={handleTriggerClick}
      >
        {renderTrigger ? (
          renderTrigger({ selected, isOpen })
        ) : (
          <TriggerContent>
            {selected?.icon}
            {selected?.label && (
              <OptionLabel>{selected.label}</OptionLabel>
            )}
          </TriggerContent>
        )}
        <Chevron isOpen={isOpen}>
          <IcomoonIcon
            icon="arrow-down-outline"
            size={10}
            color={theme.color.base.c6}
          />
        </Chevron>
      </Trigger>

      <AnimatePresence>
        {isOpen && (
          <Menu
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={listVariants}
            fullWidth={fullWidth}
          >
            {options.map(option => (
              <OptionButton
                key={option.value}
                isSelected={option.value === value}
                type="button"
                disabled={option.disabled}
                title={option.label}
                variants={optionVariants}
                onClick={() => handleSelect(option)}
              >
                {option.icon}
                {option.label && (
                  <OptionLabel>{option.label}</OptionLabel>
                )}
              </OptionButton>
            ))}
          </Menu>
        )}
      </AnimatePresence>
    </Container>
  )
}

export default Dropdown
